import { useUserIdentity } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { forIPad } from '@src/utility/styled';
import { observer } from 'mobx-react-lite';
import { type FC } from 'react';
import styled, { css } from 'styled-components';

import { KARAOKE_TRACK_NUMBER } from '../../stores/VideoCreatorStore';
import AiProducerIcon from '../../svgs/AiProducerIcon';
import KaraokeIcon from '../../svgs/KaraokeIcon';
import MediaIcon from '../../svgs/MediaIcon';
import {
  AIProducerCard,
  DesignCard,
  KaraokeCard,
  MediaCard,
  SidebarOption,
} from '../../types.ts/general';
import { useFlagsCombination } from '../../utility/useFlagsCombination';
import { HistoryIcon, TextIcon } from '../common/icons';
import { useCompositionContext } from './CompositionContext';

const SidePanelMenu: FC = observer(() => {
  const {
    categories,
    selectedCategoryId,
    setSelectedCategoryId,
    setSubcategoryFilter,
  } = useCompositionContext();
  const videoCreator = useVideoCreatorStore();
  const userIdentity = useUserIdentity();
  const {
    enableAiStoryProducer,
    creatorStudioEnableDesignTabs,
    creatorStudioEnableCompositionsTab,
  } = useFlagsCombination(userIdentity.currentRole);
  const {
    sidebarOptions,
    aiProducerSubMenu,
    mediaSubMenu,
    designSubMenu,
    karaokeSubMenu,
  } = videoCreator;
  const isProducer = sidebarOptions === SidebarOption.aiProducer;
  const isMedia = sidebarOptions === SidebarOption.media;
  const isHistory = sidebarOptions === SidebarOption.history;
  const isKaraoke = sidebarOptions === SidebarOption.karaoke;
  const isDesign = sidebarOptions === SidebarOption.design;
  const activeElement = videoCreator.getActiveElement();

  const isVideoReady = videoCreator.isVideoCreatorReady;

  const handleSetOption = (state: SidebarOption) => {
    videoCreator.sidebarOptions = state;
    videoCreator.setActiveElements();
  };

  const handleSetAICard = (state: AIProducerCard) => {
    videoCreator.aiProducerSubMenu = state;
    videoCreator.setActiveElements();
  };

  const handleSetMediaCard = (state: MediaCard) => {
    videoCreator.mediaSubMenu = state;
    videoCreator.setActiveElements();
  };

  const handleSetDesignCard = (state: DesignCard) => {
    videoCreator.designSubMenu = state;
    videoCreator.setActiveElements();
  };

  const handleSetKaraokeCard = (state: KaraokeCard) => {
    videoCreator.karaokeSubMenu = state;
    videoCreator.setActiveElements();
  };

  const ProducerCards: FC<{
    enableAiStoryProducer: boolean;
    aiProducerSubMenu: AIProducerCard;
    onSetAICard: (card: AIProducerCard) => void;
  }> = ({ enableAiStoryProducer, aiProducerSubMenu, onSetAICard }) => (
    <Cards>
      {enableAiStoryProducer && (
        <Card
          onClick={() => onSetAICard(AIProducerCard.story)}
          isSelected={aiProducerSubMenu === AIProducerCard.story}
        >
          Story
        </Card>
      )}
      <Card
        onClick={() => onSetAICard(AIProducerCard.photo)}
        isSelected={aiProducerSubMenu === AIProducerCard.photo}
      >
        Photo
      </Card>
      <Card
        onClick={() => onSetAICard(AIProducerCard.music)}
        isSelected={aiProducerSubMenu === AIProducerCard.music}
      >
        Music
      </Card>
      <Card
        onClick={() => onSetAICard(AIProducerCard.clip)}
        isSelected={aiProducerSubMenu === AIProducerCard.clip}
      >
        Clip
      </Card>
    </Cards>
  );

  const KaraokeCards: FC<{
    karaokeSubMenu: KaraokeCard;
    onSetKaraokeCard: (card: KaraokeCard) => void;
  }> = ({ karaokeSubMenu, onSetKaraokeCard }) => (
    <Cards>
      <Card
        onClick={() => onSetKaraokeCard(KaraokeCard.templates)}
        isSelected={karaokeSubMenu === KaraokeCard.templates}
      >
        Templates
      </Card>
      <Card
        onClick={() => onSetKaraokeCard(KaraokeCard.styles)}
        isSelected={karaokeSubMenu === KaraokeCard.styles}
      >
        Styles
      </Card>
      <Card
        onClick={() => onSetKaraokeCard(KaraokeCard.animations)}
        isSelected={karaokeSubMenu === KaraokeCard.animations}
      >
        Animations
      </Card>
    </Cards>
  );

  const MediaCards: FC<{
    mediaSubMenu: MediaCard;
    onSetMediaCard: (card: MediaCard) => void;
  }> = ({ mediaSubMenu, onSetMediaCard }) => (
    <Cards>
      <Card
        onClick={() => onSetMediaCard(MediaCard.photo)}
        isSelected={mediaSubMenu === MediaCard.photo}
      >
        Photo
      </Card>
      <Card
        onClick={() => onSetMediaCard(MediaCard.music)}
        isSelected={mediaSubMenu === MediaCard.music}
      >
        Music
      </Card>
      <Card
        onClick={() => onSetMediaCard(MediaCard.video)}
        isSelected={mediaSubMenu === MediaCard.video}
      >
        Video
      </Card>
    </Cards>
  );
  const DesignCards: FC<{
    creatorStudioEnableDesignTabs: boolean;
    creatorStudioEnableCompositionsTab: boolean;
    designSubMenu: DesignCard;
    onSetDesignCard: (card: DesignCard) => void;
  }> = ({
    creatorStudioEnableDesignTabs,
    creatorStudioEnableCompositionsTab,
    designSubMenu,
    onSetDesignCard,
  }) => {
    if (!creatorStudioEnableDesignTabs) {
      return null;
    }

    const cards = [
      <Card
        key={DesignCard.text}
        onClick={() => {
          onSetDesignCard(DesignCard.text);
          setSelectedCategoryId(null);
          setSubcategoryFilter(null);
        }}
        isSelected={designSubMenu === DesignCard.text}
      >
        Text
      </Card>,
      <Card
        key={DesignCard.shape}
        onClick={() => {
          onSetDesignCard(DesignCard.shape);
          setSelectedCategoryId(null);
          setSubcategoryFilter(null);
        }}
        isSelected={designSubMenu === DesignCard.shape}
      >
        Shapes
      </Card>,
    ];

    if (creatorStudioEnableCompositionsTab) {
      categories.forEach(category => {
        cards.push(
          <Card
            key={category.id}
            onClick={() => {
              onSetDesignCard(DesignCard.composition);
              setSelectedCategoryId(category.id);
              setSubcategoryFilter(null);
            }}
            isSelected={selectedCategoryId === category.id}
          >
            {category.label}
          </Card>,
        );
      });
    }

    return (
      <HorizontalScrollWrapper>
        <HorizontalScrollContent>
          <Cards style={{ flexWrap: 'nowrap' }}>{cards}</Cards>
        </HorizontalScrollContent>
      </HorizontalScrollWrapper>
    );
  };

  return (
    <Main>
      <TopMenu>
        <Icon
          onClick={() => {
            handleSetOption(SidebarOption.aiProducer);
          }}
        >
          <AiProducerIcon strokeColor={isProducer ? '#f2d093' : '#949494'} />
          <Text isSelected={isProducer}>AI Producer</Text>
        </Icon>
        <Icon
          onClick={() => {
            handleSetOption(SidebarOption.karaoke);
            videoCreator.setShowSubtitles(true);
          }}
          disabled={!isVideoReady}
          role="button"
          tabIndex={0}
          aria-disabled={!isVideoReady}
        >
          <KaraokeIcon strokeColor={isKaraoke ? '#f2d093' : '#949494'} />
          <Text isSelected={isKaraoke}>Karaoke</Text>
        </Icon>
        <Icon
          onClick={() => {
            handleSetOption(SidebarOption.media);
          }}
        >
          <MediaIcon strokeColor={isMedia ? '#f2d093' : '#949494'} />
          <Text isSelected={isMedia}>Media</Text>
        </Icon>

        <Icon
          onClick={async () => {
            if (creatorStudioEnableDesignTabs) {
              handleSetOption(SidebarOption.design);
              if (videoCreator.sidebarOptions !== SidebarOption.design) {
                handleSetDesignCard(DesignCard.text);
              }
            } else {
              if (
                videoCreator.sidebarOptions !== SidebarOption.design &&
                (activeElement?.source.type !== 'text' ||
                  activeElement?.source.track === KARAOKE_TRACK_NUMBER)
              ) {
                videoCreator.textProducer.setDefaultTextConfig();
              }

              videoCreator.sidebarOptions = SidebarOption.design;
              videoCreator.setActiveElements();
            }
          }}
          disabled={!isVideoReady}
          role="button"
          tabIndex={0}
          aria-disabled={!isVideoReady}
        >
          <TextIcon
            height="20"
            width="20"
            fillColor={isDesign ? '#f2d093' : '#949494'}
          />
          <Text isSelected={isDesign}>Design</Text>
        </Icon>

        <Icon
          onClick={() => {
            handleSetOption(SidebarOption.history);
          }}
        >
          <HistoryIcon
            height="20"
            width="20"
            strokeColor={isHistory ? '#f2d093' : '#949494'}
          />

          <Text isSelected={isHistory}>History</Text>
        </Icon>
      </TopMenu>
      {isProducer && (
        <ProducerCards
          enableAiStoryProducer={enableAiStoryProducer}
          aiProducerSubMenu={aiProducerSubMenu}
          onSetAICard={handleSetAICard}
        />
      )}
      {isKaraoke && (
        <KaraokeCards
          karaokeSubMenu={karaokeSubMenu}
          onSetKaraokeCard={handleSetKaraokeCard}
        />
      )}
      {isMedia && (
        <MediaCards
          mediaSubMenu={mediaSubMenu}
          onSetMediaCard={handleSetMediaCard}
        />
      )}
      {isDesign && (
        <DesignCards
          creatorStudioEnableDesignTabs={creatorStudioEnableDesignTabs}
          creatorStudioEnableCompositionsTab={
            creatorStudioEnableCompositionsTab
          }
          designSubMenu={designSubMenu}
          onSetDesignCard={handleSetDesignCard}
        />
      )}
      <HorizontalLine height="1" fill="none">
        <path d="M0 1L312 1.00002" stroke="#484848" />
      </HorizontalLine>
    </Main>
  );
});

export default SidePanelMenu;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 10px;
  gap: 20px;
`;

const TopMenu = styled.div`
  display: flex;
  width: 100%;
  gap: 20px;
  align-items: flex-end;

  ${forIPad(`
    gap: 10px;
    white-space: nowrap;
  `)}
`;

const Icon = styled.div<{ disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  cursor: pointer;
  border-radius: 8px;
  ${props =>
    props.disabled &&
    css`
      pointer-events: none;
      opacity: 0.5;
      cursor: not-allowed;
    `}
  span {
    font-size: 11px;
  }
`;

const Cards = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-self: start;
  white-space: nowrap;
`;

const Card = styled.div<{ isSelected?: boolean }>`
  border: 1px solid ${props => (props.isSelected ? '#f2d093' : '#5D5D5D')};
  border-radius: 4px;
  font-size: 12px;
  padding: 8px 10px;
  text-align: center;
  color: ${props => (props.isSelected ? '#030419' : '#949494')};
  background-color: ${props => (props.isSelected ? '#f2d093' : '')};
  cursor: pointer;
  ${props =>
    !props.isSelected &&
    css`
      &:hover {
        color: #f2d093;
        border-color: #f2d093;
      }
    `}
`;

const Text = styled.span<{ isSelected: boolean }>`
  color: ${props => (props.isSelected ? '#f2d093' : '#949494')};
`;

const HorizontalLine = styled.svg`
  width: 100%;
`;

// Horizontal Scroll
const HorizontalScrollWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const HorizontalScrollContent = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  overflow-y: visible;
  padding-bottom: 10px;
  padding-right: 10px;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #404040;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-track {
    background: #1a1a1a;
  }
`;
